import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, VatInvoice, HkdInfo, ImportOrder } from "../api";
import { formatVND, formatDate } from "../utils";
import { exportVatInvoices } from "../utils/exportVatInvoices";
import { GlassPanel, KpiCard, EmptyState, SkeletonTable, ErrorState, Modal, toast } from "../components";
import ImportAttachments from "../components/ImportAttachments";
import PurchaseLedgerPage from "./PurchaseLedgerPage";
import TaxReportPage from "./TaxReportPage";
import VirtualInventoryPage from "./VirtualInventoryPage";

type TabKey = "bangke" | "vat-invoices" | "s1a" | "kho-ao";

const TABS: { key: TabKey; label: string; icon: string }[] = [
  { key: "bangke", label: "Bảng kê 01/TNDN", icon: "list_alt" },
  { key: "vat-invoices", label: "Hóa đơn nhập VAT", icon: "receipt" },
  { key: "s1a", label: "Sổ S1A", icon: "description" },
  { key: "kho-ao", label: "Kho ảo (VAT/Bảng kê)", icon: "category" },
];

function VatInvoicesTab() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<VatInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [detailOrder, setDetailOrder] = useState<ImportOrder | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ supplier_name: "", supplier_tax_id: "", supplier_address: "", supplier_phone: "", supplier_bank_account: "", invoice_no: "" });
  const [saving, setSaving] = useState(false);
  const [hkd, setHkd] = useState<HkdInfo>({ name: "", tax_code: "", address: "" });

  const openDetail = async (importOrderId: number) => {
    setDetailLoading(true);
    try {
      const res = await api.importOrders.list();
      const found = (res.orders || []).find((o) => o.id === importOrderId);
      setDetailOrder(found || null);
    } catch { setDetailOrder(null); }
    finally { setDetailLoading(false); }
  };

  useEffect(() => {
    loadData();
    api.hkdInfo.get().then(setHkd).catch(() => {});
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.vatInvoices.list();
      setInvoices(res.invoices || []);
    } catch (e: any) {
      setError(e.message || "Lỗi tải dữ liệu hóa đơn VAT");
    } finally {
      setLoading(false);
    }
  };

  const totalVat = invoices.reduce((s, i) => s + (i.vat_amount || 0), 0);
  const totalAmount = invoices.reduce((s, i) => s + (i.amount_before_vat || 0), 0);

  const exportCsv = () => {
    if (!invoices.length) return;
    const headers = ["Số HĐ", "Ngày", "Người bán", "Tiền trước thuế", "VAT", "Tổng"];
    const rows = invoices.map((inv) => [
      inv.invoice_number || "",
      inv.invoice_date || "",
      inv.seller_name || "",
      inv.amount_before_vat?.toString() || "0",
      inv.vat_amount?.toString() || "0",
      ((inv.amount_before_vat || 0) + (inv.vat_amount || 0)).toString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `hoa-don-vat-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <SkeletonTable rows={6} cols={5} />;
  if (error) return <ErrorState error={error} onRetry={loadData} />;

  return (
    <div>
      {/* KPI */}
      <div className="kpi-row">
        <KpiCard label="Số hóa đơn" value={invoices.length} />
        <KpiCard label="Tổng VAT" value={formatVND(totalVat)} />
        <KpiCard label="Tổng tiền hàng" value={formatVND(totalAmount)} />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", margin: "12px 0", gap: 8 }}>
        <button className="btn" onClick={() => exportVatInvoices({ hkd, invoices })} disabled={!invoices.length}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, marginRight: 4 }}>download</span>
          Xuất Excel (khai thuế)
        </button>
      </div>

      {/* Table */}
      {invoices.length === 0 ? (
        <EmptyState icon="receipt" title="Chưa có hóa đơn VAT đầu vào nào." />
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Số HĐ</th>
                <th>Ngày</th>
                <th>Người bán</th>
                <th className="text-right">Tiền trước thuế</th>
                <th className="text-right">VAT</th>
                <th className="text-right">Tổng</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td>{inv.invoice_number || "—"}</td>
                  <td>{formatDate(inv.invoice_date)}</td>
                  <td>{inv.seller_name || "—"}</td>
                  <td className="text-right">{formatVND(inv.amount_before_vat || 0)}</td>
                  <td className="text-right">{formatVND(inv.vat_amount)}</td>
                  <td className="text-right">{formatVND((inv.amount_before_vat || 0) + (inv.vat_amount || 0))}</td>
                  <td className="text-right">
                    {inv.import_order_id ? (
                      <button
                        className="btn btn-ghost"
                        title="Xem đơn nhập"
                        style={{ padding: "2px 8px" }}
                        disabled={detailLoading}
                        onClick={() => openDetail(inv.import_order_id!)}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>visibility</span>
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!detailOrder} onClose={() => { setDetailOrder(null); setEditMode(false); }} title={detailOrder ? `Đơn nhập #${detailOrder.id}` : ""}>
        {detailOrder && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 14 }}>
            {!editMode ? (
              <>
                <div className="kv-row"><div className="key">Nhà cung cấp</div><div className="right">{detailOrder.supplier_name || "—"}</div></div>
                {detailOrder.supplier_tax_id && <div className="kv-row"><div className="key">MST / CCCD</div><div className="right">{detailOrder.supplier_tax_id}</div></div>}
                {detailOrder.supplier_address && <div className="kv-row"><div className="key">Địa chỉ</div><div className="right">{detailOrder.supplier_address}</div></div>}
                {detailOrder.supplier_phone && <div className="kv-row"><div className="key">SĐT</div><div className="right">{detailOrder.supplier_phone}</div></div>}
                {detailOrder.supplier_bank_account && <div className="kv-row"><div className="key">STK</div><div className="right">{detailOrder.supplier_bank_account}</div></div>}                <div className="kv-row"><div className="key">Ngày đặt</div><div className="right">{formatDate(detailOrder.order_date)}</div></div>
                <div className="kv-row"><div className="key">Tổng tiền</div><div className="right" style={{ fontWeight: 700 }}>{formatVND(detailOrder.total_amount)}</div></div>
                {detailOrder.vat_rate ? <div className="kv-row"><div className="key">Thuế suất VAT</div><div className="right">{detailOrder.vat_rate}%</div></div> : null}
                {detailOrder.invoice_no && <div className="kv-row"><div className="key">Số hóa đơn</div><div className="right">{detailOrder.invoice_no}</div></div>}
                {/* Action buttons */}
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => {
                    setEditForm({
                      supplier_name: detailOrder.supplier_name || "",
                      supplier_tax_id: detailOrder.supplier_tax_id || "",
                      supplier_address: detailOrder.supplier_address || "",
                      supplier_phone: detailOrder.supplier_phone || "",
                      supplier_bank_account: detailOrder.supplier_bank_account || "",
                      invoice_no: detailOrder.invoice_no || "",
                    });
                    setEditMode(true);
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>edit</span>
                    Sửa thông tin người bán
                  </button>
                  <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => navigate(`/warehouse/import?order=${detailOrder.id}`)}>
                    <span className="material-symbols-outlined" style={{ fontSize: 14 }}>link</span>
                    Mở đơn nhập gốc
                  </button>
                </div>
              </>
            ) : (
              /* Edit mode form */
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Tên NCC</label>
                <input value={editForm.supplier_name} onChange={(e) => setEditForm({ ...editForm, supplier_name: e.target.value })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>MST / CCCD</label>
                <input value={editForm.supplier_tax_id} onChange={(e) => setEditForm({ ...editForm, supplier_tax_id: e.target.value })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Địa chỉ</label>
                <input value={editForm.supplier_address} onChange={(e) => setEditForm({ ...editForm, supplier_address: e.target.value })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>SĐT</label>
                <input value={editForm.supplier_phone} onChange={(e) => setEditForm({ ...editForm, supplier_phone: e.target.value })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>STK ngân hàng</label>
                <input value={editForm.supplier_bank_account} onChange={(e) => setEditForm({ ...editForm, supplier_bank_account: e.target.value })} />
                <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Số hóa đơn</label>
                <input value={editForm.invoice_no} onChange={(e) => setEditForm({ ...editForm, invoice_no: e.target.value })} />
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button className="btn" disabled={saving} onClick={async () => {
                    setSaving(true);
                    try {
                      await api.importOrders.update(detailOrder.id, {
                        supplier_name: editForm.supplier_name || null,
                        supplier_tax_id: editForm.supplier_tax_id || null,
                        supplier_address: editForm.supplier_address || null,
                        supplier_phone: editForm.supplier_phone || null,
                        supplier_bank_account: editForm.supplier_bank_account || null,
                        invoice_no: editForm.invoice_no || null,
                      } as any);
                      toast.success("Đã lưu thông tin người bán");
                      setEditMode(false);
                      await loadData();
                      await openDetail(detailOrder.id);
                    } catch (e: any) {
                      toast.error(e.message || "Lỗi cập nhật");
                    } finally { setSaving(false); }
                  }}>
                    {saving ? "Đang lưu..." : "Lưu"}
                  </button>
                  <button className="btn btn-ghost" onClick={() => setEditMode(false)}>Huỷ</button>
                </div>
              </div>
            )}
            {/* Items */}
            {!editMode && detailOrder.items && detailOrder.items.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Sản phẩm ({detailOrder.items.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {detailOrder.items.map((it, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13, padding: "3px 0", borderBottom: "1px solid var(--border-subtle,#222)" }}>
                      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.item_name}</span>
                      <span style={{ color: "var(--text-tertiary)" }}>{it.quantity} x {formatVND(it.unit_cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Attachments */}
            {!editMode && (
              <div style={{ marginTop: 12 }}>
                <ImportAttachments orderId={detailOrder.id} />
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default function TaxCenterPage() {
  const [activeTab, setActiveTab] = useState<TabKey>(() => {
    const saved = localStorage.getItem("tax_center_tab");
    if (saved && TABS.some((t) => t.key === saved)) return saved as TabKey;
    return "bangke";
  });

  useEffect(() => {
    localStorage.setItem("tax_center_tab", activeTab);
  }, [activeTab]);

  const renderTab = () => {
    switch (activeTab) {
      case "bangke":
        return <PurchaseLedgerPage />;
      case "vat-invoices":
        return <VatInvoicesTab />;
      case "s1a":
        return <TaxReportPage />;
      case "kho-ao":
        return <VirtualInventoryPage />;
    }
  };

  return (
    <div>
      <GlassPanel>
        <h3 style={{ margin: "0 0 4px" }}>Trung tâm Thuế</h3>
        <p style={{ margin: 0, color: "var(--text-2)", fontSize: 13 }}>
          Báo cáo &amp; chứng từ khai thuế: Bảng kê 01/TNDN, hóa đơn VAT đầu vào, Sổ S1A đầu ra.
        </p>
      </GlassPanel>

      {/* Tab bar */}
      <div className="tabs" style={{ marginTop: 16 }}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? "tab-item active" : "tab-item"}
            onClick={() => setActiveTab(tab.key)}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4, verticalAlign: "middle" }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ marginTop: 16 }}>
        {renderTab()}
      </div>
    </div>
  );
}
